import { ChevronDownIcon, ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanGroupData } from 'actions'
import cloneDeep from 'clone-deep'
import { type InputType, type IVisibleProp, FieldOrigin, MultiPropertyFields, vacantOptions } from 'config'
import { customOptionLabels, editableFieldOptions, getInitialLoanFields } from 'config/loan.constants'
import type { IMultipleProperty } from 'pages/LoanStructure/interfaces'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { updateLoanFields } from 'services'
import { Select } from 'stories/components'
import { confirm, removeComma, thousandSeperatorWithPrefix } from 'utils'
import { getAdditionalPropertyFieldProps, getAdditionalPropertyInvisibleFields, setAdditionalPropertyDefaultData } from 'utils/loan'
import { RenderInput } from 'utils/RenderInput'

/**
 * Derived on the Loan Structure screen from the property's own figures, not stored on it, so there
 * is nothing here to enter or save.
 */
const DERIVED_FIELDS = ['estimatedDscr']

/**
 * Read inside the component, not at module scope: an imported const read while modules are still
 * loading has come back empty in a production bundle before, and an empty field list here would
 * render every property as a card with nothing in it.
 */
const propertyFieldKeys = () => MultiPropertyFields.filter((key) => !DERIVED_FIELDS.includes(key))

/**
 * Inputs whose change is already the committed value: a select, a Yes/No, and the address field,
 * which is written by its own dialog. These save on change; everything else saves on blur.
 */
const COMMIT_ON_CHANGE = ['select', 'Select', 'check', 'Check', 'toggle', 'Toggle', 'Map', 'map', 'checkbox', 'CheckBox']

/** Values a text input hands back comma-formatted, which the loan row must not keep. */
const NUMERIC_TYPES = ['thousandSep', 'thousandSepNoDecimal', 'number']

/** A money field reaches the loan row as a number, and an emptied one as null rather than "". */
const normalize = (input: InputType | undefined, raw: any) => {
  const isNumeric = NUMERIC_TYPES.includes(String(input?.type))
  if (raw === null || raw === undefined || raw === '') return isNumeric ? null : raw
  if (isNumeric) return removeComma(raw)
  return typeof raw === 'string' ? raw.trim() : raw
}

/**
 * The loan's additional properties, on the servicing side of the file.
 *
 * A blanket loan is secured by more than one property, but servicing only ever showed the subject
 * property: the extra parcels were enterable in origination and nowhere else, so a loan that took
 * on another property after funding had no way to record it. This is the same list origination
 * edits - `loan.multipleProperties`, with the same fields, labels and per-tenant visibility - so a
 * property added here is the same property the loan file, the documents (`P2…` placeholders), the
 * Loan Master Report and the Payoff collateral register already read. The collateral register seeds
 * itself from this list on every read, so a parcel added here turns up there without a sync step.
 *
 * Property 1 is the subject property on the loan row itself; it is shown for context and is not
 * editable or removable from here, which is why the cards start at Property 2 exactly as they do
 * in origination.
 */
export default function AdditionalProperties({ canEdit }: { canEdit: boolean }) {
  const dispatch = useDispatch()

  const { loan, maxProperties, allowCents, optionProps, propertyProps } = useSelector((state: any) => ({
    loan: state.loan,
    maxProperties: Number(state.loanDetail?.loanGlobalSettings?.maxMultipleProperties) || 0,
    allowCents: !!state.loanDetail?.loanGlobalSettings?.allowCentForLoanAmount,
    // Option lists a tenant has rewritten (property types, occupancy, …) live on the field origin;
    // the per-property config carries that field's tooltip and whether it shows at all.
    optionProps: (state.globalConfigReducer[GlobalConfigType.LoanFields]?.[FieldOrigin.LoanStructure] ||
      {}) as Record<string, IVisibleProp>,
    propertyProps: getAdditionalPropertyFieldProps(state.globalConfigReducer[GlobalConfigType.LoanFields]),
  }))

  const [rows, setRows] = useState<IMultipleProperty[]>([])
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  const [saving, setSaving] = useState(false)

  /**
   * A Yes/No field saves itself a quarter-second after it is clicked, and a select when focus
   * leaves it, so the handler that runs may belong to an earlier render. Everything that writes
   * reads the list through this ref rather than the render's own copy.
   */
  const rowsRef = useRef<IMultipleProperty[]>([])
  rowsRef.current = rows

  /** The last list written to the loan, so a blur that follows a save does not write it again. */
  const savedRef = useRef<string>('[]')

  // The loan row is the source of truth; local state only holds keystrokes until the next blur.
  const storedProperties: IMultipleProperty[] = useMemo(
    () => (Array.isArray(loan?.multipleProperties) ? loan.multipleProperties : []),
    [loan?.multipleProperties],
  )

  useEffect(() => {
    setRows(cloneDeep(storedProperties))
    savedRef.current = JSON.stringify(storedProperties)
  }, [storedProperties])

  /**
   * The Loan Structure field schema, which carries each field's label, input type and the tenant's
   * own option lists. Taking it from there rather than redeclaring it is what keeps a property
   * entered in servicing identical to one entered in origination.
   */
  const propertyInputs = useMemo(() => {
    const fields = getInitialLoanFields(false, allowCents) as Record<string, InputType>
    propertyFieldKeys().forEach((key) => {
      if (!fields[key]) return
      // A renamed list is stored as option objects, which a select cannot render; it wants the
      // stored value mapped to the name the tenant gave it.
      const renamed = customOptionLabels(editableFieldOptions(key, optionProps))
      if (renamed) fields[key].options = renamed
      else if (optionProps[key]?.options) fields[key].options = optionProps[key].options
      const tooltip = propertyProps?.[key]?.tooltip
      if (tooltip) fields[key].tooltip = tooltip
    })
    return fields
  }, [allowCents, optionProps, propertyProps])

  const fieldsByProperty = useMemo(
    () =>
      rows.map((property) => {
        const hidden = getAdditionalPropertyInvisibleFields(0, property) || []
        return propertyFieldKeys().filter((key) => !!propertyInputs[key] && !hidden.includes(key))
      }),
    [rows, propertyInputs],
  )

  const atMax = maxProperties > 0 && rows.length + 1 >= maxProperties

  /**
   * Write the whole list back, with the loan's two multi-property flags kept in step: the Loan
   * Structure screen reads `isMultipleProperties` to decide whether to show the list at all, and
   * warns when `howManyProperties` disagrees with how many are actually on file.
   */
  const save = async (next: IMultipleProperty[]) => {
    const json = {
      multipleProperties: next,
      isMultipleProperties: next.length > 0,
      howManyProperties: next.length ? next.length + 1 : 1,
    }
    setSaving(true)
    savedRef.current = JSON.stringify(next)
    try {
      await updateLoanFields(json)
      dispatch(setLoanGroupData(json))
    } catch {
      toast('Could not save the properties.', { type: 'error' })
      savedRef.current = JSON.stringify(storedProperties)
      setRows(cloneDeep(storedProperties))
    } finally {
      setSaving(false)
    }
  }

  const onAdd = async () => {
    if (atMax) {
      toast(`This loan is limited to ${maxProperties} properties.`, { type: 'error' })
      return
    }
    const property = setAdditionalPropertyDefaultData({
      id: Date.now(),
      subjectPropertyAddress: null,
      proposedOccupancy: null,
      propertyHasBeenListed: null,
      asIsValue: null,
      secondLien: null,
      secondLienPayment: null,
      lienPayoff: null,
      propertyType: null,
      condoType: null,
      numberOfUnits: null,
      proposedMonthlyRent: null,
      proposedMonthlyTaxes: null,
      proposedMonthlyInsurance: null,
      proposedMonthlyHoaDues: null,
      floodInsurance: null,
      schoolTax: null,
      otherTax: null,
    }) as IMultipleProperty
    const next = [...rowsRef.current, property]
    setRows(next)
    setExpanded((prev) => ({ ...prev, [property.id]: true }))
    await save(next)
  }

  const onRemove = async (index: number) => {
    const property = rowsRef.current[index]
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Remove this property from the loan?
        <br />
        <span className="text-gray-600 text-base">
          Property Address: {property?.subjectPropertyAddress || 'N/A'}
        </span>
        <br />
        <span className="text-gray-600 text-sm">
          This does not release any lien. A released parcel is recorded through a partial payoff.
        </span>
      </div>
    )
    if (!(await confirm(content))) return
    const next = rowsRef.current.filter((_, i) => i !== index)
    setRows(next)
    await save(next)
  }

  const onChange = (index: number, key: string, value: any) => {
    const next = cloneDeep(rowsRef.current)
    ;(next[index] as any)[key] = value
    rowsRef.current = next
    setRows(next)
    // A picked value is already the final one, and its input may never report a blur: the address
    // dialog writes the field and closes, and a Yes/No only fakes a blur a quarter-second later.
    // Typed text and figures still save on blur, so a half-typed number is not written per keystroke.
    if (COMMIT_ON_CHANGE.includes(String(propertyInputs[key]?.inputType))) save(next)
  }

  /** Saves on blur, like the rest of this screen, and only when the value actually moved. */
  const onBlur = async (index: number, key: string) => {
    const raw = (rowsRef.current[index] as any)?.[key]
    const value = normalize(propertyInputs[key], raw)
    const next = cloneDeep(rowsRef.current)
    ;(next[index] as any)[key] = value
    rowsRef.current = next
    setRows(next)
    // Against the last list actually written rather than the loan in the store, which a save that
    // is still in flight has not updated yet.
    if (JSON.stringify(next) === savedRef.current) return
    await save(next)
  }

  const summaryOf = (property: IMultipleProperty) => {
    const value = removeComma(property?.asIsValue ?? 0)
    const parts = [property?.propertyType, value ? thousandSeperatorWithPrefix(value) : '']
    return parts.filter(Boolean).join(' · ')
  }

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center border-b border-gray-200 pb-1">
        <div className="text-[16px] font-variation-settings-600">Additional Properties</div>
        {canEdit && (
          <div
            className={`flex items-center gap-1 text-sm ${
              saving ? 'text-gray-400' : 'text-shade-blue hover:underline hover:cursor-pointer'
            }`}
            onClick={() => !saving && onAdd()}
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add Property</span>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-600 mt-2">
        <span className="font-variation-settings-600">Property 1 (Subject Property): </span>
        <span>{loan?.subjectPropertyAddress || 'N/A'}</span>
      </div>

      {!rows.length ? (
        <div className="text-sm text-gray-400 mt-2">
          No additional properties. A blanket loan secured by more than one property lists each of them here.
        </div>
      ) : (
        rows.map((property, index) => {
          const isOpen = !!expanded[property.id]
          return (
            <div key={property.id ?? index} className="mt-3 rounded border border-solid border-gray-200 shadow1 p-2">
              <div className="flex justify-between items-center gap-2">
                <div
                  className="flex items-center gap-1 hover:cursor-pointer"
                  onClick={() => setExpanded((prev) => ({ ...prev, [property.id]: !isOpen }))}
                >
                  {isOpen ? (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                  )}
                  <span className="text-sm font-variation-settings-600">Property {index + 2}</span>
                  <span className="text-sm text-gray-600">
                    {property?.subjectPropertyAddress || 'No address yet'}
                  </span>
                  <span className="text-xs text-gray-400">{summaryOf(property)}</span>
                </div>
                {canEdit && (
                  <div
                    className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded"
                    onClick={() => onRemove(index)}
                  >
                    <TrashIcon className="w-4 h-4 hover:cursor-pointer text-red-700" />
                  </div>
                )}
              </div>

              {isOpen && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  {fieldsByProperty[index]?.map((key) => {
                    const input = propertyInputs[key]
                    const value = (property as any)[key]
                    let additionalElements = null
                    let prefix = input.prefix

                    // Vacancy is entered either as a count of units or as a percentage of them, so
                    // the number means nothing without the unit beside it.
                    if (key === 'vacant') {
                      prefix = property.vacantIsPercentage ? '%' : ''
                      additionalElements = (
                        <span onClick={(event) => event.stopPropagation()}>
                          <Select
                            id={`vacant-unit-${property.id}`}
                            size={3}
                            className="-mb-4 w-fit"
                            borderClassName="!pr-5 !text-[11px] !text-slate-500 leading-[16px] rounded hover:cursor-pointer"
                            options={vacantOptions}
                            value={property.vacantIsPercentage ? 'Percentage' : 'Number'}
                            onChange={(unit: string) => {
                              const next = cloneDeep(rowsRef.current)
                              ;(next[index] as any).vacantIsPercentage = unit === 'Percentage'
                              setRows(next)
                              save(next)
                            }}
                          />
                        </span>
                      )
                    }

                    return (
                      <div key={key} className={input.length === 1 ? 'md:col-span-2' : 'md:col-span-1'}>
                        <RenderInput
                          input={{
                            ...input,
                            value: value ?? '',
                            prefix,
                            additionalElements,
                            error: '',
                            disabled: !canEdit || saving,
                            readOnly: !canEdit,
                          }}
                          Key={key}
                          onChange={(k: string, v: any) => onChange(index, k, v)}
                          onBlur={(k: string) => onBlur(index, k)}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}
